import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import {
  generateCustomAttributeTypes,
  getActionOptions,
  getConditionOptions,
  getCustomAttributeInputType,
  getOperatorTypes,
  isACustomAttribute,
  getFileName,
  getDefaultConditions,
  getDefaultActions,
  filterCustomAttributes,
  generateAutomationPayload,
  getStandardAttributeInputType,
  isCustomAttribute,
  generateCustomAttributes,
} from 'dashboard/helper/automationHelper';
import { mapGetters } from 'vuex';
import {
  OPPORTUNITY_TEMPERATURES,
  temperatureLabelKey,
} from 'dashboard/helper/opportunityTemperatureHelper';
import {
  OPPORTUNITY_ACTIVITY_TYPES,
  activityTypeLabelKey,
} from 'dashboard/helper/opportunityActivityHelper';

const COMPOSITE_ACTION_INPUT_TYPES = [
  'opportunity_activity',
  'create_opportunity',
];

export default {
  computed: {
    ...mapGetters({
      agents: 'agents/getAgents',
      campaigns: 'campaigns/getAllCampaigns',
      contacts: 'contacts/getContacts',
      inboxes: 'inboxes/getInboxes',
      labels: 'labels/getLabels',
      teams: 'teams/getTeams',
      templates: 'whatsappTemplates/getTemplates',
      macros: 'macros/getMacros',
      closingReasons: 'closingReasons/getClosingReasons',
      setCloseReason: 'closingReasons/getClosingReasons',
      accountEvents: 'events/getEvents',
      pipelines: 'opportunityPipelines/getPipelines',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    opportunityTemperatureOptions() {
      return OPPORTUNITY_TEMPERATURES.map(temperature => ({
        id: temperature,
        name: this.$t(temperatureLabelKey(temperature)),
      }));
    },
    opportunityActivityTypeOptions() {
      return OPPORTUNITY_ACTIVITY_TYPES.map(type => ({
        id: type,
        name: this.$t(activityTypeLabelKey(type)),
      }));
    },
    booleanFilterOptions() {
      return [
        {
          id: true,
          name: this.$t('FILTER.ATTRIBUTE_LABELS.TRUE'),
        },
        {
          id: false,
          name: this.$t('FILTER.ATTRIBUTE_LABELS.FALSE'),
        },
      ];
    },

    statusFilterOptions() {
      const statusFilters = this.$t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS');
      return [
        ...Object.keys(statusFilters).map(status => {
          return {
            id: status,
            name: statusFilters[status].TEXT,
          };
        }),
      ];
    },
    availableActionTypes() {
      const allowedKeys = (
        this.automationTypes[this.automation?.event_name]?.actions || []
      ).map(action => action.key);
      return this.automationActionTypes.filter(
        type =>
          allowedKeys.includes(type.key) &&
          (!type.featureFlag ||
            this.isFeatureEnabledonAccount(this.accountId, type.featureFlag))
      );
    },
  },
  methods: {
    getFileName,
    getSelectableActionTypes(currentActionName) {
      const isAvailable = this.availableActionTypes.some(
        type => type.key === currentActionName
      );
      const currentActionType = this.automationActionTypes.find(
        type => type.key === currentActionName
      );
      if (isAvailable || !currentActionType) return this.availableActionTypes;
      return [...this.availableActionTypes, currentActionType];
    },
    onEventChange() {
      this.automation.conditions = getDefaultConditions(
        this.automation.event_name
      );
      this.automation.actions = getDefaultActions(this.automation.event_name);
    },
    getAttributes(key) {
      return this.automationTypes[key].conditions;
    },
    getInputType(key) {
      const customAttribute = isACustomAttribute(this.allCustomAttributes, key);
      if (customAttribute) {
        return getCustomAttributeInputType(
          customAttribute.attribute_display_type
        );
      }
      const type = this.getAutomationType(key);
      return type.inputType;
    },
    getOperators(key) {
      if (this.mode === 'edit') {
        const customAttribute = isACustomAttribute(
          this.allCustomAttributes,
          key
        );
        if (customAttribute) {
          return getOperatorTypes(customAttribute.attribute_display_type);
        }
      }

      if (key === 'is_template') {
        const type = this.getAutomationType(key);

        if (type && Array.isArray(type.filterOperators) && type.filterOperators.length) {
          return [type.filterOperators[0]];
        }
        return [
          {
            value: 'equal_to',
            label: 'Equal to',
          },
        ];
      }

      const type = this.getAutomationType(key);
      return type.filterOperators;
    },
    getAutomationType(key) {
      return this.automationTypes[this.automation.event_name].conditions.find(
        condition => condition.key === key
      );
    },
    getCustomAttributeType(key) {
      const type = this.automationTypes[
        this.automation.event_name
      ].conditions.find(i => i.key === key).customAttributeType;
      return type;
    },
    getConditionDropdownValues(type) {
      const {
        agents,
        allCustomAttributes: customAttributes,
        booleanFilterOptions,
        campaigns,
        contacts,
        inboxes,
        statusFilterOptions,
        teams,
        labels,
        closingReasons,
        accountEvents,
        pipelines
      } = this;

      if (type === 'is_template') {
        return [
          { id: true, name: this.$t('FILTER.ATTRIBUTE_LABELS.YES') },
          { id: false, name: this.$t('FILTER.ATTRIBUTE_LABELS.NO') },
        ];
      }

      if (type === 'internal_note') {
        return [
          { id: true, name: this.$t('FILTER.ATTRIBUTE_LABELS.TRUE') },
          { id: false, name: this.$t('FILTER.ATTRIBUTE_LABELS.FALSE') },
        ];
      }

      return getConditionOptions({
        agents,
        booleanFilterOptions,
        campaigns,
        contacts,
        customAttributes,
        inboxes,
        statusFilterOptions,
        teams,
        languages,
        countries,
        type,
        labels,
        closingReasons,
        accountEvents: (accountEvents || []).map(e => ({ id: e.id, name: e.event_name })),
        pipelines,
        temperatureOptions: this.opportunityTemperatureOptions,
        activityTypeOptions: this.opportunityActivityTypeOptions,
      });
    },
    appendNewCondition() {
      this.automation.conditions.push(
        ...getDefaultConditions(this.automation.event_name)
      );
    },
    appendNewAction() {
      this.automation.actions.push(
        ...getDefaultActions(this.automation.event_name)
      );
    },
    removeFilter(index) {
      if (this.automation.conditions.length <= 1) {
        this.showAlert(this.$t('AUTOMATION.CONDITION.DELETE_MESSAGE'));
      } else {
        this.automation.conditions.splice(index, 1);
      }
    },
    removeAction(index) {
      if (this.automation.actions.length <= 1) {
        this.showAlert(this.$t('AUTOMATION.ACTION.DELETE_MESSAGE'));
      } else {
        this.automation.actions.splice(index, 1);
      }
    },
    submitAutomation() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      const automation = generateAutomationPayload(this.automation);
      this.$emit('saveAutomation', automation, this.mode);
    },
    resetFilter(index, currentCondition) {
      this.automation.conditions[index].filter_operator = this.automationTypes[
        this.automation.event_name
      ].conditions.find(
        condition => condition.key === currentCondition.attribute_key
      ).filterOperators[0].value;
      this.automation.conditions[index].values = '';
    },
    showUserInput(type) {
      return !(type === 'is_present' || type === 'is_not_present');
    },
    showActionInput(action) {
      if (
        action === 'send_email_to_team' ||
        action === 'send_message' ||
        action === 'send_private_message' ||
        action === 'create_opportunity_note'
      )
        return false;
      const type = this.automationActionTypes.find(i => i.key === action)
        .inputType;
      return !!type && !COMPOSITE_ACTION_INPUT_TYPES.includes(type);
    },
    resetAction(index) {
      this.automation.actions[index].action_params = [];
    },
    manifestConditions(automation) {
      const customAttributes = filterCustomAttributes(this.allCustomAttributes);
      const conditions = automation.conditions.map(condition => {
        const customAttr = isCustomAttribute(
          customAttributes,
          condition.attribute_key
        );

        if (condition.attribute_key === 'hour_of_day') {
          return {
            ...condition,
            values: condition.values[0],
            query_operator: condition.query_operator || 'and',
          };
        }

        let inputType = 'plain_text';
        if (customAttr) {
          inputType = getCustomAttributeInputType(customAttr.type);
        } else {
          inputType = getStandardAttributeInputType(
            this.automationTypes,
            automation.event_name,
            condition.attribute_key
          );
        }
        if (
          inputType === 'plain_text' ||
          inputType === 'date' ||
          inputType === 'number'
        ) {
          return {
            ...condition,
            values: condition.values[0],
          };
        }
        return {
          ...condition,
          query_operator: condition.query_operator || 'and',
          values: [
            ...this.getConditionDropdownValues(condition.attribute_key),
          ].filter(item => [...condition.values].includes(item.id)),
        };
      });
      return conditions;
    },
    generateActionsArray(action) {
      const params = action.action_params;
      let actionParams = [];
      const inputType = this.automationActionTypes.find(
        item => item.key === action.action_name
      ).inputType;
      
      if (
        inputType === 'team_message' &&
        (
          action.action_name === 'add_custom_attribute' ||
          action.action_name === 'add_contact_custom_attribute'
        )
      ) {
        actionParams = {
          team_ids: [
            ...this.getActionDropdownValues(action.action_name),
          ].filter(item => item.id === params[0].team_ids[0]),
          message: params[0].message,
        };
      } else if (COMPOSITE_ACTION_INPUT_TYPES.includes(inputType)) {
        actionParams = { ...params[0] };
      } else if (inputType === 'multi_select' || inputType === 'search_select') {
        actionParams = [
          ...this.getActionDropdownValues(action.action_name),
        ].filter(item => [...params].includes(item.id));
      } else if (inputType === 'team_message') {
        actionParams = {
          team_ids: [
            ...this.getActionDropdownValues(action.action_name),
          ].filter(item => [...params[0].team_ids].includes(item.id)),
          message: params[0].message,
        };
      } else actionParams = [...params];
      
      return actionParams;
    },
    manifestActions(automation) {
      // A shared variable here would leak one action's params into the next.
      const actions = automation.actions.map(action => {
        const actionParams = action.action_params.length
          ? this.generateActionsArray(action)
          : [];
        return {
          ...action,
          action_params: actionParams,
        };
      });
      return actions;
    },
    formatAutomation(automation) {
      this.automation = {
        ...automation,
        conditions: this.manifestConditions(automation),
        actions: this.manifestActions(automation).map(action => {
          if (
            ['send_whatsapp_message', 'send_whatsapp_message_now']
              .includes(action.action_name)
          ) {
            const templateBody = action.action_params?.template_body;
            if (templateBody) {
              this.automation.actions[0].action_params.template_body = templateBody;
            }
          }
          return action;
        }),
      };
    },
    getActionDropdownValues(type) {
      const conversationCustomAttributes = this.$store.getters[
        'attributes/getAttributesByModel'
      ]('conversation_attribute');

      const contactCustomAttributes = this.$store.getters[
        'attributes/getAttributesByModel'
      ]('contact_attribute');

      const customAttributes = generateCustomAttributeTypes(
        conversationCustomAttributes,
        'conversation_attribute'
      ).map(attr => ({
        id: attr.key,
        name: attr.name,
        visibility: 'global',
        inputType: attr.inputType,
        filterOperators: attr.filterOperators || [],
      }));

      const customContactAttributes = generateCustomAttributeTypes(
        contactCustomAttributes,
        'contact_attribute'
      ).map(attr => ({
        id: attr.key,
        name: attr.name,
        visibility: 'global',
        inputType: attr.inputType,
        filterOperators: attr.filterOperators || [],
      }));

      const { agents, labels, teams, inboxes, templates, macros, campaigns, closingReasons, setCloseReason, accountEvents, pipelines } = this;

      return getActionOptions({
        agents,
        labels,
        teams,
        inboxes,
        templates,
        languages,
        type,
        macros,
        campaigns,
        customAttributes,
        customContactAttributes,
        closingReasons,
        setCloseReason,
        accountEvents,
        pipelines,
        temperatureOptions: this.opportunityTemperatureOptions,
        activityTypeOptions: this.opportunityActivityTypeOptions,
      });
    },
    manifestCustomAttributes() {
      const conversationCustomAttributesRaw = this.$store.getters[
        'attributes/getAttributesByModel'
      ]('conversation_attribute');

      const contactCustomAttributesRaw = this.$store.getters[
        'attributes/getAttributesByModel'
      ]('contact_attribute');
      const conversationCustomAttributeTypes = generateCustomAttributeTypes(
        conversationCustomAttributesRaw,
        'conversation_attribute'
      );
      const contactCustomAttributeTypes = generateCustomAttributeTypes(
        contactCustomAttributesRaw,
        'contact_attribute'
      );
      let manifestedCustomAttributes = generateCustomAttributes(
        conversationCustomAttributeTypes,
        contactCustomAttributeTypes,
        this.$t('AUTOMATION.CONDITION.CONVERSATION_CUSTOM_ATTR_LABEL'),
        this.$t('AUTOMATION.CONDITION.CONTACT_CUSTOM_ATTR_LABEL')
      );
      this.automationTypes.message_created.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.conversation_created.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.conversation_updated.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.conversation_opened.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.conversation_status_changed.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.conversation_tag_updated.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.conversation_team_updated.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.conversation_agent_updated.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.conversation_priority_updated.conditions.push(
        ...manifestedCustomAttributes
      );
    },
  },
};
